import { useEffect, useState } from "react";
import { MessageCircle, FileText, Menu, X } from "lucide-react";
import { CONTACT } from "@/lib/data";
import { LANGS } from "@/lib/i18n";
import { useI18n } from "@/context/LanguageContext";
import { scrollToId } from "@/hooks/useLenis";

const LangSwitch = ({ scrolled }) => {
  const { lang, setLang } = useI18n();
  return (
    <div className={`flex border ${scrolled ? "border-charcoal/30" : "border-white/25"}`} data-testid="lang-switch">
      {LANGS.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`lang-${l.code}`}
          className={`px-2 sm:px-2.5 h-8 font-mono2 text-xs font-700 transition-colors ${
            lang === l.code
              ? "bg-safety text-white"
              : scrolled ? "text-charcoal hover:text-safety" : "text-white/70 hover:text-safety"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV = [
    { label: t.nav.services, href: "#services" },
    { label: t.nav.capabilities, href: "#capabilities" },
    { label: t.nav.portfolio, href: "#portfolio" },
    { label: t.nav.quality, href: "#quality" },
    { label: t.nav.contact, href: "#quote" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md border-b-2 border-charcoal"
          : "bg-charcoal/80 backdrop-blur-sm border-b border-white/10"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-2 sm:gap-4">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2 sm:gap-3 group min-w-0">
          <div className="stripes w-9 h-9 flex items-center justify-center border-2 border-charcoal flex-shrink-0">
            <span className={`font-display font-700 text-lg ${scrolled ? "text-charcoal" : "text-white"} mix-blend-difference`}>N</span>
          </div>
          <div className="leading-none min-w-0">
            <div className={`font-display font-700 text-base sm:text-lg tracking-wide truncate ${scrolled ? "text-charcoal" : "text-white"}`}>
              NIDHI<span className="text-safety"> FABRICATION</span>
            </div>
            <div className={`font-mono2 text-[10px] tracking-[0.3em] hidden sm:block ${scrolled ? "text-charcoal/50" : "text-white/50"}`}>
              {t.brandSub}
            </div>
          </div>
        </a>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n, i) => (
            <a
              key={i}
              href={n.href}
              data-testid={`nav-${["services", "capabilities", "portfolio", "quality", "contact"][i]}`}
              className={`font-mono2 text-xs font-500 tracking-wider uppercase transition-colors hover:text-safety ${scrolled ? "text-charcoal" : "text-white/80"}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LangSwitch scrolled={scrolled} />
          <a
            href={`https://wa.me/${CONTACT.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="flex items-center gap-2 px-4 h-10 border-2 border-safety text-safety font-mono2 text-xs font-700 uppercase tracking-wider hover:bg-safety hover:text-white transition-colors"
          >
            <MessageCircle size={16} /> {t.cta.whatsapp}
          </a>
          <button
            onClick={() => scrollToId("#quote")}
            data-testid="header-quote-btn"
            className="flex items-center gap-2 px-5 h-10 bg-safety text-white font-mono2 text-xs font-700 uppercase tracking-wider hover:translate-x-[2px] hover:translate-y-[2px] transition-transform hard-shadow-sm"
          >
            <FileText size={16} /> {t.cta.rapidQuote}
          </button>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 lg:hidden flex-shrink-0">
          <LangSwitch scrolled={scrolled} />
          <button
            className={`${scrolled ? "text-charcoal" : "text-white"}`}
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-charcoal border-t border-white/10 px-5 py-6 space-y-4" data-testid="mobile-menu">
          {NAV.map((n, i) => (
            <a key={i} href={n.href} onClick={() => setOpen(false)} className="block font-display text-2xl text-white hover:text-safety">
              {n.label}
            </a>
          ))}
          <div className="flex gap-3 pt-4">
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex-1 text-center py-3 border-2 border-safety text-safety font-mono2 text-xs font-700 uppercase">
              {t.cta.whatsapp}
            </a>
            <button onClick={() => { setOpen(false); scrollToId("#quote"); }} className="flex-1 py-3 bg-safety text-white font-mono2 text-xs font-700 uppercase">
              {t.cta.rapidQuote}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
